import { supabase, ServiceResponse, handleServiceCall } from './supabase';

export interface Inspection {
    id: string;
    batch_id: string;
    inspector_id: string;
    result: 'passed' | 'failed' | 'flagged';
    photos: string[];
    notes?: string;
    transit_weight?: number;
    transit_length?: number;
    transit_width?: number;
    transit_height?: number;
    check_weight?: number;
    check_length?: number;
    check_width?: number;
    check_height?: number;
    created_at: string;
}

export const InspectionService = {
    /**
     * Internal helper to check if a batch is locked for mutations
     */
    async _checkBatchLock(batchId: string): Promise<void> {
        const { data: batch, error } = await supabase
            .from('batches')
            .select('status')
            .eq('id', batchId)
            .single();

        if (error) throw new Error('无法验证批次状态');

        const { data: userData } = await supabase.auth.getUser();
        if (!userData.user) return;

        const { data: profile } = await supabase
            .from('profiles')
            .select('role')
            .eq('id', userData.user.id)
            .single();

        const role = profile?.role;
        let lockedStatuses: string[] = ['completed', 'cancelled'];

        if (role === 'sender') {
            lockedStatuses = ['completed', 'cancelled', 'sender_sealed', 'transit_processing', 'transit_sealed', 'receiver_processing', 'sealed', 'in_transit', 'inspected', 'received'];
        } else if (role === 'transit') {
            lockedStatuses = ['completed', 'cancelled', 'transit_sealed', 'receiver_processing', 'received'];
        } else if (role === 'receiver') {
            lockedStatuses = ['completed', 'cancelled'];
        }

        if (lockedStatuses.includes(batch.status)) {
            throw new Error(`该批次已处于“${batch.status}”状态，当前角色不可修改。`);
        }
    },

    /**
     * Create Inspection Report
     */
    async create(data: Partial<Inspection> & { batch_id: string; result: Inspection['result'] }): Promise<ServiceResponse<Inspection>> {
        try {
            await this._checkBatchLock(data.batch_id);
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return { data: null, error: 'Not authenticated', success: false };

            return handleServiceCall(
                supabase.from('inspections').insert({
                    ...data,
                    inspector_id: user.id
                }).select().single()
            );
        } catch (e: any) {
            return { data: null, error: e.message, success: false };
        }
    },

    /**
     * Get Inspections for a Batch
     */
    async getByBatch(batchId: string): Promise<ServiceResponse<Inspection[]>> {
        return handleServiceCall(
            supabase.from('inspections')
                .select('*')
                .eq('batch_id', batchId)
                .order('created_at', { ascending: false })
        );
    },

    /**
     * Update Inspection
     */
    async update(id: string, updates: Partial<Inspection>): Promise<ServiceResponse<Inspection>> {
        try {
            let batchId = updates.batch_id;
            if (!batchId) {
                const { data } = await supabase.from('inspections').select('batch_id').eq('id', id).single();
                batchId = data?.batch_id;
            }
            if (batchId) await this._checkBatchLock(batchId);

            return handleServiceCall(
                supabase.from('inspections').update(updates).eq('id', id).select().single()
            );
        } catch (e: any) {
            return { data: null, error: e.message, success: false };
        }
    }
};
